import {ChangeDetectionStrategy, Component, computed} from '@angular/core'
import {map, mergeMap} from 'rxjs/operators'
import {ActivatedRoute} from '@angular/router'
import {Observable, of, zip} from 'rxjs'
import {OfferingPageResolved} from './offering-page.resolver'
import {OptableLayout} from '../../../core/components/layout/optable-layout.interface'
import {PUBLIC_ROUTES} from '../../../shared/routing/public.routes'
import {LinkService} from '../../../core/i18n/link.service'
import {Breadcrumb} from '../../../craft/breadcrumbs/breadcrumbs.component'
import {PageComponent, PageMetadata} from '../../../core/seo/page-metadata'
import {TranslocoService} from '@ngneat/transloco'
import {DialogService} from '../../../craft/dialog/dialog.service'
import {faInfoCircle} from '@fortawesome/pro-solid-svg-icons/faInfoCircle'
import {HappinessDialogComponent} from '../home-page/banner/happinees-dialog/happiness-dialog.component'
import {Language} from '../../../core/i18n/language'
import {toSignal} from '@angular/core/rxjs-interop'
import {CraftsmenRegionPagesContentService} from '../../../core/content/services/craftsmen-region-pages-content.service'

export const FILTER_BY_REGION_QUERY_PARAM = 'filterByRegion'

@Component({
  selector: 'bgo-offering-page',
  templateUrl: './offering-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OfferingPageComponent extends PageComponent implements OptableLayout {
  readonly optOutOfLayout = true
  readonly transparentNavbar = false
  readonly page$: Observable<OfferingPageResolved>
  readonly breadcrumbs$: Observable<{
    showBackButton: boolean
    breadcrumbs: Breadcrumb[]
  }>

  readonly infoIcon = faInfoCircle

  private regionSlug = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get(FILTER_BY_REGION_QUERY_PARAM))),
    {initialValue: null},
  )

  region = computed(() => {
    const slug = this.regionSlug()
    if (slug) {
      return this.regionService.getRegion(slug)
    }
  })

  constructor(
    protected readonly linkService: LinkService,
    private readonly translocoService: TranslocoService,
    private readonly dialogService: DialogService,
    private readonly route: ActivatedRoute,
    private readonly regionService: CraftsmenRegionPagesContentService,
  ) {
    super()
    this.page$ = route.data.pipe(map(data => data['content']))
    this.breadcrumbs$ = this.page$.pipe(
      map(page => {
        const breadcrumbs: Breadcrumb[] = [
          ...(page.industryPage
            ? [
                {
                  name: page.industryPage.title,
                  link: linkService.localizedPathTo(PUBLIC_ROUTES.INDUSTRY_OVERVIEW_PAGE, {
                    routeParams: {pageSlug: page.industryPage.slug},
                  }),
                },
              ]
            : // The name "Back" is only here to satisfy the interface. The actual name is handled in the `cft-back-button`
              [{name: 'Back', link: linkService.previousPage ?? linkService.homePath}]),
          {
            name: page.title,
            link: linkService.localizedPathTo(PUBLIC_ROUTES.OFFERING_PAGE, {
              routeParams: {pageSlug: page.slug ?? page.id},
            }),
          },
        ]
        return {
          showBackButton: !page.industryPage,
          breadcrumbs,
        }
      }),
    )
  }

  navigateToAboutUsPage(): Promise<boolean> {
    return this.linkService.navigateTo(PUBLIC_ROUTES.ABOUT_US)
  }

  getPageMetadata(): Observable<PageMetadata> {
    return this.page$.pipe(
      mergeMap(page =>
        zip([
          of(page),
          this.translocoService.selectTranslate('offeringDirectory.metadata.title', {offering: page.title}),
          this.translocoService.selectTranslate('offeringDirectory.metadata.description', {offering: page.title}),
        ]),
      ),
      map(([page, defaultTitle, defaultDescription]) => ({
        title: page.seo?.title || defaultTitle,
        description: page.seo?.description || defaultDescription,
        doNotIndexPage: page.seo?.doNotIndex ?? false,
        canonicalUrl: page.seo?.canonicalUrl,
      })),
    )
  }

  showHappinessDialog(): void {
    this.dialogService.show(HappinessDialogComponent, {
      text: this.translocoService.translate('shared.sellingPoints.happiness.modal.text'),
    })
  }

  getPageAvailableLanguages(): Observable<Language[] | undefined> {
    return this.page$.pipe(map(p => p.seo?.availableLanguages))
  }

  protected readonly faInfoCircle = faInfoCircle
}
